import { useCallback, useEffect, useReducer, useState } from 'react';

import axios, { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer, useContainer } from 'unstated-next';

import { USERS } from '../../config/apiPaths';
import {
    CreateUserRequestInput,
    CreateUsersRequestInput,
    PaginatedUsers,
    Pagination,
    Status,
    UpdateUserRequestInput,
    UsersRecord,
    UsersRecordExtended
} from '../../types/apiTypes';
import { getUrls } from '../../utils/url';
import AdminAuthContainer from './AdminAuthContainer';
import { LoadingStatus, UserFromCSV } from './components/AdminUsers/types';
import { UserRole } from './constants/user-roles';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { getUserUuidHeaders } from '../../utils/httpUtils';

export type UpdateUserInput = Required<UsersRecord> & { password?: string };

interface State {
    page: number;
    perPage: number;
    roleFilter: UserRole | null;
    searchText: string;
}

type Action =
    | { type: 'SET_PAGE'; payload: number }
    | { type: 'SET_PER_PAGE'; payload: number }
    | { type: 'SET_ROLE_FILTER'; payload: UserRole | null }
    | { type: 'SET_SEARCH_TEXT'; payload: string };

const initialState: State = {
    page: 1,
    perPage: 50,
    roleFilter: null,
    searchText: ''
};

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'SET_PAGE':
            return { ...state, page: action.payload };
        case 'SET_PER_PAGE':
            return { ...state, perPage: action.payload, page: 1 };
        case 'SET_ROLE_FILTER':
            return { ...state, roleFilter: action.payload, page: 1 };
        case 'SET_SEARCH_TEXT':
            return { ...state, searchText: action.payload, page: 1 };
        default:
            return state;
    }
}

const useAdminUsers = () => {
    const { t } = useTranslation('admin_settings');
    const [status, setStatus] = useState<Status>(Status.NotStarted);
    const [error, setError] = useState<boolean>(false);
    const [users, setUsers] = useState<UsersRecordExtended[]>([]);
    const { uuid } = useContainer(AdminAuthContainer);

    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([]);
    const [usersForBulkUpload, setUsersForBulkUpload] = useState<UserFromCSV[]>([]);
    const [errorIndexes, setErrorIndexes] = useState<number[]>([]);
    const [maxUsersLimitExceeded, setMaxUsersLimitExceeded] = useState<boolean>(false);
    const [errorsAcknowledged, setErrorsAcknowledged] = useState(false);
    const [duplicateIndexes, setDuplicateIndexes] = useState<number[]>([]);
    const [bulkUpsertUsersStatus, setBulkUpsertUsersStatus] = useState<LoadingStatus>('INITIAL');
    const [fileNameForCSV, setFileNameForCSV] = useState('');

    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [state, dispatch] = useReducer(reducer, initialState);

    const { enqueueSnackbar } = useSnackbar();

    const { api } = getUrls();

    useSchoolInfo();

    const resetBulkUploadState = () => {
        setFileNameForCSV('');
        setUsersForBulkUpload([]);
        setBulkUpsertUsersStatus('INITIAL');
        setSelectedRoles([]);
        setErrorIndexes([]);
        setDuplicateIndexes([]);
        setErrorsAcknowledged(false);
    };

    const setPage = (newPage) => {
        dispatch({ type: 'SET_PAGE', payload: newPage });
    };

    const setPerPage = (newPerPage) => {
        dispatch({ type: 'SET_PER_PAGE', payload: newPerPage });
    };

    const setRoleFilter = (newRoleFilter) => {
        dispatch({ type: 'SET_ROLE_FILTER', payload: newRoleFilter });
    };

    const setSearchText = (newSearchText) => {
        dispatch({ type: 'SET_SEARCH_TEXT', payload: newSearchText });
    };

    /**
     * Fetches a list of all users for an organization.
     */
    const fetchUsers = useCallback(async () => {
        if (!uuid) return;
        const { page, perPage, roleFilter, searchText } = state;

        try {
            setStatus(Status.InProgress);
            setError(false);

            const params = {
                page,
                limit: perPage,
                roles: roleFilter || undefined,
                search: searchText || undefined
            };

            const headers = getUserUuidHeaders(uuid);

            const result = await axios.get<PaginatedUsers>(`${api}${USERS}`, { params, headers });

            setUsers(result.data.data);
            setPagination(result.data.pagination);
        } catch (err) {
            setError(true);
        }

        setStatus(Status.Complete);
    }, [api, state, uuid]);

    /**
     * Adds a single user
     */
    const addUser = async (newUser: CreateUserRequestInput) => {
        try {
            setStatus(Status.InProgress);

            const { email, name, roles } = newUser;
            const postData: CreateUsersRequestInput = {
                user_uuid: uuid,
                users: [{
                    email,
                    name,
                    roles
                }]
            };

            await axios.post(`${api}${USERS}`, postData);

            const successMessage = t('users.actions.add-user-success', { userName: newUser.name });
            enqueueSnackbar(successMessage, { variant: 'success' });

            fetchUsers();
        } catch (err) {
            const errorMessage = t('users.actions.add-user-error', { userName: newUser.name });
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }

        setStatus(Status.Complete);
    };

    /**
     * Updates a single user.
     */
    const updateUser = async (updates: UpdateUserInput) => {
        setStatus(Status.InProgress);

        try {
            const postData: UpdateUserRequestInput = {
                user_uuid: uuid,
                email: updates.email,
                name: updates.user_name,
                roles: updates.roles,
                user_id: updates.user_id,
                password: updates.password
            };

            await axios.put(`${api}${USERS}`, postData);

            enqueueSnackbar(t('users.actions.edit-user-success'), { variant: 'success' });

            fetchUsers();
        } catch (err) {
            let errorMessage: string;

            if ((err as AxiosError).response && (err as AxiosError).response?.status === 409) {
                errorMessage = t('users.actions.edit-user-error-email', { email: updates.email });
            } else {
                errorMessage = t('users.actions.edit-user-error');
            }

            enqueueSnackbar(errorMessage, { variant: 'error' });
        }

        setStatus(Status.Complete);
    };

    /**
     * Adds users in bulk and creates/updates users
     * based on email matches.
     */
    const bulkUpsertUsers = async (newUsers: CreateUserRequestInput[]) => {
        setStatus(Status.InProgress);

        try {
            const payload: CreateUsersRequestInput = {
                user_uuid: uuid,
                users: newUsers
            };

            await axios.post(`${api}${USERS}`, payload);

            enqueueSnackbar(t('admin-invite.admins-added-success'), { variant: 'success' });

            resetBulkUploadState();

            fetchUsers();
        } catch (err) {
            const genericMessage = t('admin-invite.generic-upload-error');
            enqueueSnackbar(genericMessage, { variant: 'error' });
        }

        setStatus(Status.Complete);
    };

    /**
     * Deletes a user from an organization.
     */
    const deleteUser = async ({ user_id, user_name }: UsersRecord) => {
        setStatus(Status.InProgress);

        const data = {
            user_ids: [user_id],
            user_uuid: uuid
        };

        try {
            await axios.delete(`${api}${USERS}`, { data });

            const successMessage = t('users.actions.delete-user-success', { userName: user_name });
            enqueueSnackbar(successMessage, { variant: 'success' });

            fetchUsers();
        } catch (err) {
            const errorMessage = t('users.actions.delete-user-error');
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }

        setStatus(Status.Complete);
    };

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    return {
        users,
        status,
        error,
        fetchUsers,

        page: state.page,
        perPage: state.perPage,
        roleFilter: state.roleFilter,
        searchText: state.searchText,

        setPage,
        setPerPage,
        setRoleFilter,
        setSearchText,

        pagination,

        addUser,
        updateUser,
        deleteUser,

        selectedRoles,
        setSelectedRoles,

        usersForBulkUpload,
        setUsersForBulkUpload,

        errorIndexes,
        setErrorIndexes,
        duplicateIndexes,
        setDuplicateIndexes,
        errorsAcknowledged,
        setErrorsAcknowledged,
        maxUsersLimitExceeded,
        setMaxUsersLimitExceeded,

        fileNameForCSV,
        setFileNameForCSV,

        bulkUpsertUsers,
        bulkUpsertUsersStatus,

        resetBulkUploadState
    };
};

const AdminUsersContainer = createContainer(useAdminUsers);

export default AdminUsersContainer;
